import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard, User, FileText, ClipboardList, Newspaper,
  BookOpen, FlaskConical, Library, FolderOpen, Settings, LogOut,
  ChevronLeft, ChevronRight, CreditCard,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { useUIStore } from '../../store/uiStore'
import { programmeLabel } from '../../utils/programme'
import AIControls from '../AIControls'

// Takes the programme because the profile link is labelled with the user's
// own degree ("DBA Profile"), not a hardcoded "PhD Profile".
const navFor = programme => [
  {
    section: 'OVERVIEW',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard',                            path: '/app/dashboard' },
      { icon: User,            label: `${programmeLabel(programme)} Profile`, path: '/app/profile' },
    ],
  },
  {
    section: 'DOCUMENTS',
    items: [
      { icon: FileText,      label: 'Statement of Purpose', path: '/app/documents/sop' },
      { icon: ClipboardList, label: 'Synopsis',              path: '/app/documents/synopsis' },
      { icon: Newspaper,     label: 'Research Paper',        path: '/app/documents/research-paper' },
      { icon: BookOpen,      label: 'Thesis',                path: '/app/thesis' },
    ],
  },
  {
    section: 'TOOLS',
    items: [
      { icon: FlaskConical, label: 'Research Assistant', path: '/app/research' },
      { icon: Library,      label: 'References',         path: '/app/references' },
      { icon: FolderOpen,   label: 'My Files',           path: '/app/files' },
    ],
  },
  {
    section: 'ACCOUNT',
    items: [
      { icon: CreditCard, label: 'Billing',  path: '/app/billing' },
      { icon: Settings,   label: 'Settings', path: '/app/settings' },
    ],
  },
]

function initials(name) {
  if (!name) return '?'
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()
}

export default function Sidebar() {
  const location  = useLocation()
  const navigate  = useNavigate()
  const { user, profile, logout } = useAuthStore()
  const { sidebarCollapsed, toggleSidebar } = useUIStore()

  // Pinned state (sidebarCollapsed) is the user's actual preference and what
  // the toggle button controls. Hovering while pinned-collapsed temporarily
  // shows the full sidebar as a floating overlay — it never pushes the main
  // content, which stays reserved at the pinned collapsed width the whole
  // time — and reverts the moment the cursor leaves.
  const [hovering, setHovering] = useState(false)
  const expanded = !sidebarCollapsed || hovering
  const overlay  = sidebarCollapsed && hovering

  const isActive = path => location.pathname.startsWith(path)

  const handleLogout = async () => {
    await logout()
    navigate('/login', { replace: true })
  }

  const displayName = profile?.full_name || user?.email?.split('@')[0] || 'Scholar'

  return (
    <aside
      className="shrink-0 relative"
      style={{ width: sidebarCollapsed ? 60 : 248 }}
      onMouseEnter={() => sidebarCollapsed && setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <div
        className="flex flex-col h-full overflow-hidden transition-[width] duration-150"
        style={{
          width: expanded ? 248 : 60,
          position: overlay ? 'absolute' : 'static',
          top: 0, left: 0,
          background: '#0f172a',
          borderRight: '1px solid rgba(255,255,255,0.06)',
          boxShadow: overlay ? '6px 0 28px rgba(0,0,0,0.4)' : 'none',
          zIndex: overlay ? 30 : 'auto',
        }}
      >
      {/* Collapse toggle — top-right corner, next to v1.0 when expanded.
          Always pins/unpins; hover-expand never touches this state. */}
      <button
        onClick={toggleSidebar}
        title={sidebarCollapsed ? 'Keep sidebar open' : 'Collapse sidebar'}
        className="absolute z-10 flex items-center justify-center rounded-md transition-colors"
        style={{ top: 10, right: expanded ? 8 : '50%', transform: expanded ? 'none' : 'translateX(50%)', width: 20, height: 20, color: '#475569' }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = '#94a3b8' }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}
      >
        {sidebarCollapsed
          ? <ChevronRight style={{ width: 13, height: 13 }} />
          : <ChevronLeft style={{ width: 13, height: 13 }} />}
      </button>

      {/* Logo */}
      <div className="flex items-center h-14 px-3 shrink-0" style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Link to="/app/dashboard" className="flex items-center gap-2.5 min-w-0 flex-1">
          <div className="w-7 h-7 rounded-lg shrink-0 flex items-center justify-center font-sans font-bold text-xs"
            style={{ background: '#2563eb', color: '#ffffff' }}>A</div>
          {expanded && (
            <div className="flex items-baseline gap-1 min-w-0">
              <span className="font-display font-semibold text-sm tracking-wide" style={{ color: '#f1f5f9' }}>AcdyON</span>
              <span className="font-display italic text-sm" style={{ color: '#60a5fa' }}>Scholar</span>
            </div>
          )}
        </Link>
        {expanded && (
          <span className="font-mono shrink-0 mr-6" style={{ fontSize: 9, color: '#475569', letterSpacing: '0.06em' }}>v1.0</span>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 scrollbar-thin">
        {navFor(profile?.programme).map(({ section, items }) => (
          <div key={section} className="mb-0.5">
            {expanded && (
              <p className="font-mono px-3.5 pt-3 pb-1" style={{ fontSize: 9, letterSpacing: '0.14em', color: '#64748b' }}>
                {section}
              </p>
            )}
            {!expanded && <div className="h-3" />}

            {items.map(({ icon: Icon, label, path }) => {
              const active = isActive(path)
              return (
                <Link
                  key={path}
                  to={path}
                  title={expanded ? undefined : label}
                  className="flex items-center gap-2.5 transition-all duration-150 relative group"
                  style={{
                    padding: expanded ? '8px 10px' : '8px 0',
                    margin: '1px 6px',
                    borderRadius: 8,
                    color:      active ? '#60a5fa' : '#94a3b8',
                    background: active ? 'rgba(37,99,235,0.15)' : 'transparent',
                    borderLeft: active ? '2px solid #60a5fa' : '2px solid transparent',
                    justifyContent: expanded ? 'flex-start' : 'center',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.05)' }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
                >
                  <Icon className="shrink-0" style={{ width: 15, height: 15, color: active ? '#60a5fa' : '#64748b' }} />
                  {expanded && (
                    <span className="font-sans text-sm truncate" style={{ fontWeight: active ? 500 : 400, color: active ? '#e2e8f0' : '#94a3b8' }}>
                      {label}
                    </span>
                  )}
                  {!expanded && (
                    <span
                      className="absolute left-full ml-2.5 px-2.5 py-1.5 rounded-lg text-xs font-sans whitespace-nowrap
                                 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50"
                      style={{ background: '#1e293b', color: '#e2e8f0', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }}
                    >
                      {label}
                    </span>
                  )}
                </Link>
              )
            })}
          </div>
        ))}
      </nav>

      {/* AI controls */}
      <AIControls collapsed={!expanded} />

      {/* User card */}
      <div>
        <div className="flex items-center gap-2.5 px-3 py-3">
          <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center font-sans font-bold text-xs"
            style={{ background: '#2563eb', color: '#ffffff' }}>
            {initials(displayName)}
          </div>
          {expanded && (
            <>
              <div className="flex-1 min-w-0">
                <p className="font-sans text-xs font-semibold truncate" style={{ color: '#e2e8f0' }}>{displayName}</p>
                <p className="font-mono text-xs truncate" style={{ color: '#475569', fontSize: 10 }}>{programmeLabel(profile?.programme)} Candidate</p>
              </div>
              <button
                onClick={handleLogout}
                title="Sign out"
                className="shrink-0 p-1.5 rounded-lg transition-all"
                style={{ color: '#475569' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.15)'; e.currentTarget.style.color = '#f87171' }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#475569' }}
              >
                <LogOut style={{ width: 13, height: 13 }} />
              </button>
            </>
          )}
        </div>
      </div>
      </div>
    </aside>
  )
}
